import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const GAP = 6;   // breathing room between the anchor and the panel
const EDGE = 8;  // keep this far from the viewport edges

/**
 * A dropdown that escapes its container's overflow.
 *
 * DataTable wraps its table in overflow:hidden (for the rounded corners) plus
 * an inner overflow-x:auto — and CSS resolves the other axis from `visible` to
 * `auto` as soon as one axis isn't visible, so the table is a closed scroll
 * box. An absolutely-positioned menu inside a cell is trapped in it: on a short
 * table the panel is hidden, and on the last row it falls below the border.
 *
 * Rendering into document.body with fixed coordinates taken from the anchor
 * sidesteps that without touching DataTable, so every other table keeps its
 * corners and its horizontal scrolling.
 *
 * Mount it only while the menu is open — mounting *is* the open state.
 */
function AnchoredMenu({ anchorRef, panelRef, width = 260, children }) {
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const flip = below < above; // open upward only when there is genuinely more room up
    // Plain pixel math against the anchor's right edge, so RTL needs no special case.
    const maxLeft = Math.max(EDGE, window.innerWidth - width - EDGE);
    setPos({
      left: Math.min(Math.max(r.right - width, EDGE), maxLeft),
      ...(flip
        ? { bottom: window.innerHeight - r.top + GAP }
        : { top: r.bottom + GAP }),
      // Last-resort guard so an expanded sub-panel can never run off screen.
      maxHeight: Math.max(160, flip ? above : below),
    });
  }, [anchorRef, width]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    // Capture phase, so scrolling in any ancestor — the table's own scroll box
    // included — keeps the panel glued to its button.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed', zIndex: 9999, width,
        overflowY: 'auto', overscrollBehavior: 'contain', borderRadius: 10,
        ...pos,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

export default AnchoredMenu;
